import { Hook, Toast, updateWidgetContent, Widget, WidgetEditor } from 'jamespot-front-business';
import jamespot, {
    CombinedWidgetContent,
    ensureWidgetArticleAttachmentType,
    ensureWidgetArticleButtonType,
    ensureWidgetArticleGalleryType,
    ensureWidgetArticleImageType,
    ensureWidgetArticleSliderType,
    ensureWidgetArticleTextType,
    ensureWidgetArticleTitleType,
    ensureWidgetCalendarCalDavType,
    ensureWidgetCheckListType,
    ensureWidgetContactCardDavType,
    ensureWidgetDatasourceTableType,
    ensureWidgetEmailsImapType,
    ensureWidgetExcelDatasourceTableType,
    ensureWidgetLuccaAbsenceType,
    ensureWidgetPresenceType,
    ensureWidgetQuickSurveyType,
    HookListType,
    WidgetArticleAttachmentContent,
    WidgetArticleButtonContent,
    WidgetArticleGalleryContent,
    WidgetArticleImageContent,
    WidgetArticleSliderContent,
    WidgetArticleTextContent,
    WidgetArticleTitleContent,
    WidgetCheckListContent,
    WidgetDatasourceTableContent,
    WidgetPresenceContent,
    WidgetQuickSurveyContent,
    WidgetStateProps,
} from 'jamespot-user-api';
import React, { ReactElement } from 'react';

import { shallowEqual } from 'react-redux';
import { commonComponentsRegistry } from '../../registry/CommonRegistry';
import { Utils } from 'jamespot-react-components';
import { store } from '../../redux/initStore';
import { useAppDispatch, useAppSelector } from '../../redux/store';

const WidgetArticleTitleEditor = commonComponentsRegistry.getLazyComponent('WidgetArticleTitleEditor');
const WidgetArticleTextEditor = commonComponentsRegistry.getLazyComponent('WidgetArticleTextEditor');
const WidgetArticleGalleryEditor = commonComponentsRegistry.getLazyComponent('WidgetArticleGalleryEditor');
const WidgetArticleAttachmentEditor = commonComponentsRegistry.getLazyComponent('WidgetArticleAttachmentEditor');
const WidgetArticleSliderEditor = commonComponentsRegistry.getLazyComponent('WidgetArticleSliderEditor');

const WidgetArticleImageEditor = commonComponentsRegistry.getLazyComponent('WidgetArticleImageEditor');
const WidgetButtonEditor = commonComponentsRegistry.getLazyComponent('WidgetButtonEditor');
const WidgetCheckListEditor = commonComponentsRegistry.getLazyComponent('WidgetCheckListEditor');
const WidgetQuickSurveyEditor = commonComponentsRegistry.getLazyComponent('WidgetQuickSurveyEditor');

const WidgetDatasourceTableEditor = commonComponentsRegistry.getLazyComponent('WidgetDatasourceTableEditor');
const WidgetExcelDatasourceTableEditor = commonComponentsRegistry.getLazyComponent('WidgetExcelDatasourceTableEditor');

const WidgetPresenceEditor = commonComponentsRegistry.getLazyComponent('WidgetPresenceEditor');

const WidgetLuccaAbsenceEditor = commonComponentsRegistry.getLazyComponent('WidgetLuccaAbsenceEditor');

const WidgetEmailsImapEditor = commonComponentsRegistry.getLazyComponent('WidgetEmailsImapEditor');
const WidgetContactCardDavEditor = commonComponentsRegistry.getLazyComponent('WidgetContactCardDavEditor');
const WidgetCalendarCalDavEditor = commonComponentsRegistry.getLazyComponent('WidgetCalendarCalDavEditor');

/*
    Editor Factory
*/
export const Editor = ({ uniqid }: { uniqid: string }) => {
    const dispatch = useAppDispatch();
    const token = useAppSelector(Widget.selectors.selectToken);
    const widgetWrapper = useAppSelector((state) => Widget.selectors.selectWidget(state, uniqid), shallowEqual);

    const onSaveCallback = useAppSelector(
        (state) => WidgetEditor.selectors.selectEditor(state, uniqid)?.onSave,
        shallowEqual,
    );

    const handleHookIsActive = (name: keyof HookListType) => {
        return Hook.selectors.selectHook(store.getState(), name)?.isActive;
    };

    const onSave = (content: Partial<CombinedWidgetContent>) => {
        /*
            Update widget & Flush editor
        */
        if (widgetWrapper) {
            dispatch(Widget.slice.actions.updateWidget({ uniqid, content }));
        }

        dispatch(WidgetEditor.slice.actions.flushEditor({ uniqid }));

        if (onSaveCallback) {
            onSaveCallback(content);
        }
    };

    const onStateChange = (states: WidgetStateProps, id?: string) => {
        /*
            Set widget state (busy, loading, initialized)
        */
        if (id !== undefined) {
            dispatch(Widget.slice.actions.setWidgetState({ uniqid: id, ...states }));
        } else {
            dispatch(Widget.slice.actions.setWidgetState({ uniqid, ...states }));
        }
    };

    const onOpen = (view: ReactElement) => {
        /*
            Register popup (central)
        */
        dispatch(WidgetEditor.slice.actions.registerEditorPopup({ uniqid, view }));
    };

    const widget = widgetWrapper?.widget;

    if (!widget) return null;

    if (ensureWidgetArticleButtonType(widget)) {
        return (
            <WidgetButtonEditor
                {...widget.content}
                onChange={(content) => {
                    dispatch(updateWidgetContent<WidgetArticleButtonContent>(uniqid, content, false));
                }}
                onSave={() => onSave({})}
            />
        );
    } else if (ensureWidgetArticleGalleryType(widget)) {
        return (
            <WidgetArticleGalleryEditor
                token={token}
                content={widget.content}
                onChange={(content) =>
                    dispatch(updateWidgetContent<WidgetArticleGalleryContent>(uniqid, content, false))
                }
                onSave={onSave}
                onStateChange={onStateChange}
            />
        );
    } else if (ensureWidgetArticleAttachmentType(widget)) {
        return (
            <WidgetArticleAttachmentEditor
                token={token}
                content={widget.content}
                onChange={(content) => {
                    dispatch(updateWidgetContent<WidgetArticleAttachmentContent>(uniqid, content, false));
                }}
                onSave={onSave}
                onStateChange={onStateChange}
                onOpen={onOpen}
            />
        );
    } else if (ensureWidgetLuccaAbsenceType(widget)) {
        return <WidgetLuccaAbsenceEditor />;
    } else if (ensureWidgetArticleImageType(widget)) {
        return (
            <WidgetArticleImageEditor
                content={widget.content}
                token={token}
                onChange={(content) => {
                    dispatch(updateWidgetContent<WidgetArticleImageContent>(uniqid, content, false));
                }}
                onSave={onSave}
                onStateChange={onStateChange}
            />
        );
    } else if (ensureWidgetArticleSliderType(widget)) {
        return token !== undefined ? (
            <WidgetArticleSliderEditor
                slides={widget.content.slides ?? []}
                loop={widget.content.loop ?? false}
                useDots={widget.content.useDots ?? true}
                onChange={(content) => {
                    dispatch(updateWidgetContent<WidgetArticleSliderContent>(uniqid, content, false));
                }}
                token={token}
                onSave={onSave}
            />
        ) : (
            <>Token is mandatory</>
        );
    } else if (ensureWidgetArticleTitleType(widget)) {
        return (
            <WidgetArticleTitleEditor
                onChange={(content) => {
                    dispatch(updateWidgetContent<WidgetArticleTitleContent>(uniqid, content, false));
                }}
                onSave={onSave}
                content={widget.content}
                onOpen={onOpen}
            />
        );
    } else if (ensureWidgetArticleTextType(widget)) {
        return (
            <WidgetArticleTextEditor
                onChange={(content) => {
                    dispatch(updateWidgetContent<WidgetArticleTextContent>(uniqid, content, false));
                }}
                onSave={onSave}
                content={widget.content}
            />
        );
    } else if (ensureWidgetPresenceType(widget)) {
        return (
            <WidgetPresenceEditor
                onChange={(content) => {
                    dispatch(updateWidgetContent<WidgetPresenceContent>(uniqid, content, false));
                }}
                onSave={onSave}
                onStateChange={onStateChange}
                {...widget.content}
            />
        );
    } else if (ensureWidgetCheckListType(widget)) {
        return (
            <WidgetCheckListEditor
                token={token}
                uniqid={widget.uniqid}
                content={{
                    ...widget.content,
                    arr: widget.content.arr?.map((w) => ({
                        ...w,
                        title: Utils.html.htmlDecodeSpecialChars(w.title),
                    })),
                }}
                onChangeContent={(uniqid, content) => {
                    dispatch(updateWidgetContent(uniqid, content, false));
                }}
                onChangeWrapper={(content) => {
                    dispatch(
                        Widget.slice.actions.updateWidgetWrapper({
                            ...content,
                            title: Utils.html.htmlDecodeSpecialChars(content.title),
                        }),
                    );
                }}
                handleChange={(content) =>
                    dispatch(updateWidgetContent<WidgetCheckListContent>(uniqid, content, false))
                }
            />
        );
    } else if (ensureWidgetQuickSurveyType(widget)) {
        return (
            <WidgetQuickSurveyEditor
                token={token}
                uniqid={widget.uniqid}
                content={widget.content}
                onChangeContent={(uniqid, content) => {
                    dispatch(updateWidgetContent<WidgetQuickSurveyContent>(uniqid, content, false));
                }}
                onChangeWrapper={(content) => {
                    dispatch(Widget.slice.actions.updateWidgetWrapper({ ...content }));
                }}
                handleChange={(content) =>
                    dispatch(updateWidgetContent<WidgetQuickSurveyContent>(uniqid, content, false))
                }
            />
        );
    } else if (ensureWidgetExcelDatasourceTableType(widget)) {
        return (
            <WidgetExcelDatasourceTableEditor
                {...widget.content}
                uri={widget.content.uri}
                limit={widget.content.limit ?? 20}
                hook={{ isActive: handleHookIsActive }}
                handlers={{
                    jamespot: {
                        datasource: jamespot.datasource,
                        network: jamespot.network,
                        filebank: jamespot.filebank,
                        wedoc: jamespot.wedoc,
                    },
                    handleStart: () => onStateChange({ busy: true }),
                    handleCatch: (err) => dispatch(Toast.actions.error({ label: err.errorMsg })),
                    handleFinally: () => onStateChange({ busy: false }),
                }}
                onSave={onSave}
                onChange={(content, override) =>
                    dispatch(updateWidgetContent<WidgetDatasourceTableContent>(uniqid, content, override ?? false))
                }
                onStateChange={onStateChange}
                onError={(label) => dispatch(Toast.actions.error({ label }))}
            />
        );
    } else if (ensureWidgetEmailsImapType(widget)) {
        return <WidgetEmailsImapEditor />;
    } else if (ensureWidgetContactCardDavType(widget)) {
        return <WidgetContactCardDavEditor />;
    } else if (ensureWidgetCalendarCalDavType(widget)) {
        return <WidgetCalendarCalDavEditor />;
    } else if (ensureWidgetDatasourceTableType(widget)) {
        return (
            <WidgetDatasourceTableEditor
                {...widget.content}
                uri={widget.content.uri}
                limit={widget.content.limit ?? 20}
                hook={{ isActive: handleHookIsActive }}
                handlers={{
                    jamespot: {
                        datasource: jamespot.datasource,
                        network: jamespot.network,
                        application: jamespot.application,
                        filebank: jamespot.filebank,
                    },
                    handleStart: () => onStateChange({ busy: true }),
                    handleCatch: (err) => dispatch(Toast.actions.error({ label: err.errorMsg })),
                    handleFinally: () => onStateChange({ busy: false }),
                }}
                onSave={onSave}
                onChange={(content, override) =>
                    dispatch(updateWidgetContent<WidgetDatasourceTableContent>(uniqid, content, override ?? false))
                }
                onStateChange={onStateChange}
                onError={(label) => {
                    dispatch(Toast.actions.error({ label }));
                }}
            />
        );
    } else {
        return <>cannot display editor</>;
    }
};
